// The measurement engine (§4): Google's text cut (step 1), the device line limit (step 2),
// mapping back to the writer's text (step 3), room left and status (step 4). Browser only.

import type { Field, FieldLayout, GoogleCut, Profile, ProfileResult, SerpSizeConfig, Status } from './types';
import { firstLineEnd, layoutLines, layoutRichLines, naturalWidth, suffixWidth, type Line } from './measure';
import { boldRuns, codePointLength, codePointOffset, shownPct, trimEndOffset } from './text';

/** Step 1: Google's device-independent cut. Titles are always measured in the desktop title font. */
export function googleCut(field: Field, original: string, config: SerpSizeConfig): GoogleCut {
  const len = original.length;
  if (field === 'title') {
    const font = config.titleFont;
    const limit = config.titleLimitPx;
    const tolLimit = limit * (1 + config.overflowTolerancePct / 100);
    const w = naturalWidth(original, font);
    if (w <= tolLimit) {
      return { cut: false, sent: original, keptEnd: len, midWord: false, withinTolerance: w > limit, widthPx: w, limitPx: limit };
    }
    const ellipsisPx = suffixWidth(config.ellipsis, font);
    const end = firstLineEnd(original, font, tolLimit - ellipsisPx);
    let keptEnd = end;
    let midWord = false;
    if (end < len && original.charCodeAt(end) !== 0x20) {
      // Pretext may break after a hyphen or inside an overlong word; Google's cut snaps to a space (M4).
      const space = original.lastIndexOf(' ', end - 1);
      if (space > 0) keptEnd = trimEndOffset(original, space);
      else midWord = true;
    }
    if (keptEnd <= 0) {
      // Not even one word fits: keep the mid-word cut.
      keptEnd = end;
      midWord = true;
    }
    const sent = original.slice(0, keptEnd) + config.ellipsis;
    return { cut: true, sent, keptEnd, midWord, withinTolerance: naturalWidth(sent, font) > limit, widthPx: w, limitPx: limit };
  }
  const cap = config.descCharCap;
  const chars = codePointLength(original);
  if (chars <= cap) {
    return { cut: false, sent: original, keptEnd: len, midWord: false, withinTolerance: false, chars, charCap: cap };
  }
  const c = codePointOffset(original, cap);
  let keptEnd: number;
  let midWord = false;
  if (original.charCodeAt(c) === 0x20) keptEnd = trimEndOffset(original, c);
  else {
    const space = original.lastIndexOf(' ', c - 1);
    if (space > 0) keptEnd = trimEndOffset(original, space);
    else {
      keptEnd = c;
      midWord = true;
    }
  }
  return { cut: true, sent: original.slice(0, keptEnd) + config.ellipsis, keptEnd, midWord, withinTolerance: false, chars, charCap: cap };
}

export interface LayoutOptions {
  query?: string;
  /** The profile whose fonts are compared for FONT_SENSITIVE. */
  twin?: Profile;
  /** Returns families missing in this engine for a font string. */
  missingFamilies?: (font: string) => string[];
}

function layoutField(sent: string, layout: FieldLayout, field: Field, query: string | undefined, config: SerpSizeConfig, width: number): { lines: Line[]; bolded: boolean } {
  if (field === 'description' && query && layout.boldFont) {
    const runs = boldRuns(sent, query, config.boldStopwords);
    if (runs.length) return { lines: layoutRichLines(sent, runs, layout.font, layout.boldFont, width), bolded: true };
  }
  return { lines: layoutLines(sent, layout.font, width), bolded: false };
}

function shownEndFor(lines: Line[], maxLines: number, sentLen: number, keptEnd: number, original: string): number {
  const visibleEndInSent = lines.length > maxLines ? lines[maxLines - 1].end : sentLen;
  return trimEndOffset(original, Math.min(visibleEndInSent, keptEnd));
}

/** Steps 2–4 for one profile, given the step-1 cut. */
export function layoutProfile(
  field: Field,
  original: string,
  cut: GoogleCut,
  profile: Profile,
  config: SerpSizeConfig,
  opts: LayoutOptions = {},
): ProfileResult {
  const layout = profile[field];
  const tol = 1 + config.overflowTolerancePct / 100;
  const sent = cut.sent;
  const { lines, bolded } = layoutField(sent, layout, field, opts.query, config, layout.width * tol);
  const maxLines = layout.maxLines;
  const visible = lines.slice(0, maxLines);
  const hidden = lines.slice(maxLines);

  const shownEnd = shownEndFor(lines, maxLines, sent.length, cut.keptEnd, original);
  const shownText = original.slice(0, shownEnd);
  const notShownText = original.slice(shownEnd).trim();
  const linesHid = shownEnd < cut.keptEnd;
  const cutBy = cut.cut ? (linesHid ? 'both' : 'google') : linesHid ? 'lines' : 'none';

  let roomLeftPx = 0;
  const toleranceUsed = visible.some(l => l.width > layout.width);
  if (!notShownText) {
    const lastWidth = visible.length ? visible[visible.length - 1].width : 0;
    roomLeftPx = (maxLines - Math.max(1, lines.length)) * layout.width + (layout.width - lastWidth);
    if (field === 'title') {
      roomLeftPx = Math.min(roomLeftPx, config.titleLimitPx - (cut.widthPx ?? 0));
    } else {
      const chars = codePointLength(original);
      const sum = lines.reduce((s, l) => s + l.width, 0);
      if (chars > 0) roomLeftPx = Math.min(roomLeftPx, (config.descCharCap - chars) * (sum / chars));
    }
    roomLeftPx = Math.max(0, roomLeftPx);
  }
  const tight = field === 'title' ? config.tightTitlePx : config.tightDescPx;
  let status: Status;
  if (notShownText) status = 'cut';
  else if (roomLeftPx < tight || toleranceUsed || cut.withinTolerance) status = 'tight';
  else status = 'fits';

  const result: ProfileResult = {
    profile: profile.id,
    profileStatus: profile.status,
    engine: profile.engine,
    status,
    cutBy,
    lines: visible.map(l => sent.slice(l.start, l.end)),
    hiddenLines: hidden.map(l => sent.slice(l.start, l.end)),
    lineWidths: lines.map(l => round2(l.width)),
    shownText,
    notShownText,
    shownEnd,
    shownPct: shownPct(original, shownEnd),
    lineCount: lines.length,
    maxLines,
    roomLeftPx: round1(roomLeftPx),
    toleranceUsed,
    bolded,
    warnings: [],
  };

  const missing = opts.missingFamilies;
  const fallback = missing ? [...new Set([...missing(layout.font), ...(bolded && layout.boldFont ? missing(layout.boldFont) : [])])] : [];
  if (fallback.length) result.fontFallback = fallback;

  // FONT_SENSITIVE input: same sent text, this profile's width and line limit, the twin's font.
  const twin = opts.twin;
  if (twin) {
    const twinLayout = twin[field];
    const differs = twinLayout.font !== layout.font || (bolded && twinLayout.boldFont !== layout.boldFont);
    const twinMissing = missing ? missing(twinLayout.font).length > 0 || (bolded && !!twinLayout.boldFont && missing(twinLayout.boldFont).length > 0) : false;
    if (differs && !twinMissing && !fallback.length) {
      const swapped: FieldLayout = { ...layout, font: twinLayout.font, boldFont: twinLayout.boldFont ?? layout.boldFont };
      const t = layoutField(sent, swapped, field, opts.query, config, layout.width * tol);
      result.twinShownEnd = shownEndFor(t.lines, maxLines, sent.length, cut.keptEnd, original);
      result.twinProfile = twin.id;
    }
  }
  return result;
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}
function round2(n: number): number {
  return Math.round(n * 100) / 100;
}
